import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useCurrency } from "@/context/CurrencyContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BarChart3, CalendarDays, Coins, ExternalLink, Building2, Pencil, Plus, ShoppingBag, Tag, Ticket, Trash2, Users, Youtube } from "lucide-react";
import { InvitesAdminTab, OneOnOneAdminTab, ReelsSettingsCard, WorkshopsAdminTab } from "@/components/admin/SankalpAdminTabs";

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const { format } = useCurrency();

  const refresh = async () => {
    const [ov, en, co, cp, iq] = await Promise.all([
      api.get("/admin/overview"), api.get("/admin/enrollments"),
      api.get("/catalog/courses"), api.get("/admin/coupons"),
      api.get("/admin/company-inquiries").catch(() => ({ data: [] })),
    ]);
    setOverview(ov.data); setEnrollments(en.data); setCourses(co.data); setCoupons(cp.data); setInquiries(iq.data);
  };
  useEffect(() => { refresh(); }, []);
  if (!overview) return <div className="min-h-screen bg-[#09090B] text-zinc-400 flex items-center justify-center">Loading…</div>;

  const kpis = [
    { label: "Bookings", value: overview.total_bookings, icon: <ShoppingBag className="w-5 h-5"/> },
    { label: "Users", value: overview.total_users, icon: <Users className="w-5 h-5"/> },
    { label: "Students & parents", value: overview.total_students, icon: <BarChart3 className="w-5 h-5"/> },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">Admin</div>
        <h1 className="font-adult-heading text-4xl md:text-5xl font-semibold tracking-tight">Command deck</h1>

        {/* Revenue cards */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(overview.revenue_by_currency).length === 0 ? (
            <div className="col-span-2 md:col-span-2 rounded-2xl border border-zinc-800 p-6 flex items-center gap-4">
              <Coins className="w-5 h-5 text-amber-400"/>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500">Revenue</div>
                <div className="font-adult-heading text-lg mt-1 text-zinc-300">No paid bookings yet.</div>
              </div>
            </div>
          ) : Object.entries(overview.revenue_by_currency).map(([cur, val]) => (
            <div key={cur} data-testid={`kpi-revenue-${cur}`} className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
              <div className="flex items-center justify-between text-amber-400">
                <div className="text-xs uppercase tracking-[0.2em] font-adult-mono">Revenue · {cur.toUpperCase()}</div>
                <Coins className="w-5 h-5"/>
              </div>
              <div className="mt-4 font-adult-heading text-3xl font-semibold">{format(val, cur)}</div>
            </div>
          ))}
          {kpis.map((k) => (
            <div key={k.label} data-testid={`kpi-${k.label.replace(/[\s&]/g,"-").toLowerCase()}`} className="rounded-2xl border border-zinc-800 p-6">
              <div className="flex items-center justify-between text-zinc-400">
                <div className="text-xs uppercase tracking-[0.2em] font-adult-mono">{k.label}</div>
                {k.icon}
              </div>
              <div className="mt-4 font-adult-heading text-3xl font-semibold">{k.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="courses" className="mt-10">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
            <TabsTrigger value="courses" data-testid="admin-tab-courses">Mini Courses</TabsTrigger>
            <TabsTrigger value="one-on-one" data-testid="admin-tab-one-on-one">1-on-1</TabsTrigger>
            <TabsTrigger value="workshops" data-testid="admin-tab-workshops">Workshops</TabsTrigger>
            <TabsTrigger value="invites" data-testid="admin-tab-invites">Invites</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="kids-events" data-testid="admin-tab-kids-events">Kids Sessions</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries">Company Inquiries</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="admin-tab-enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="admin-tab-revenue">Revenue</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6"><CoursesTab courses={courses} refresh={refresh} /></TabsContent>
          <TabsContent value="one-on-one" className="mt-6"><OneOnOneAdminTab /></TabsContent>
          <TabsContent value="workshops" className="mt-6"><WorkshopsAdminTab /></TabsContent>
          <TabsContent value="invites" className="mt-6"><InvitesAdminTab /></TabsContent>
          <TabsContent value="coupons" className="mt-6"><CouponsTab coupons={coupons} refresh={refresh} /></TabsContent>
          <TabsContent value="kids-events" className="mt-6"><KidsEventsTab /></TabsContent>
          <TabsContent value="inquiries" className="mt-6"><InquiriesTab inquiries={inquiries} /></TabsContent>
          <TabsContent value="settings" className="mt-6"><FounderSettingsTab /><div className="mt-6"><ReelsSettingsCard /></div></TabsContent>
          <TabsContent value="enrollments" className="mt-6">
            {enrollments.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-zinc-800 p-12 text-center text-zinc-400">No paid enrollments yet.</div>
            ) : (
              <div className="rounded-2xl border border-zinc-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950 text-zinc-500 text-xs uppercase tracking-[0.2em] font-adult-mono">
                    <tr><th className="text-left p-4">User</th><th className="text-left p-4">Item</th><th className="text-left p-4">Kind</th><th className="text-left p-4">Coupon</th><th className="text-right p-4">Amount</th></tr>
                  </thead>
                  <tbody>
                    {enrollments.map((r) => (
                      <tr key={r.booking_id} className="border-t border-zinc-900">
                        <td className="p-4">{r.user?.name || r.user?.email}</td>
                        <td className="p-4 text-zinc-400">{r.item_id}</td>
                        <td className="p-4 text-zinc-400">{r.item_kind}</td>
                        <td className="p-4 text-zinc-500 font-adult-mono">{r.coupon_code || "—"}</td>
                        <td className="p-4 text-right font-adult-mono">{format(r.amount, r.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
          <TabsContent value="revenue" className="mt-6">
            <div className="rounded-2xl border border-zinc-800 p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500 mb-4">Revenue by product</div>
              {Object.entries(overview.revenue_by_kind).length === 0 ? (
                <div className="text-sm text-zinc-500">No revenue yet.</div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(overview.revenue_by_kind).map(([k, v]) => {
                    const cur = k.match(/\(([A-Z]+)\)/)?.[1]?.toLowerCase() || "usd";
                    return (
                      <div key={k} className="flex items-center justify-between">
                        <div className="text-sm">{k}</div>
                        <div className="font-adult-mono text-amber-400">{format(v, cur)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer variant="adult" />
    </div>
  );
}

function CoursesTab({ courses, refresh }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", thumbnail: "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=1200", price_inr: 999, price_usd: 12, duration_weeks: 2, level: "Beginner" });
  const [editing, setEditing] = useState(null);

  const create = async () => {
    try { await api.post("/admin/courses", { ...form, lessons: [], tags: [] }); toast.success("Course created"); setCreating(false); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    try {
      await api.patch(`/admin/courses/${editing.course_id}`, {
        title: editing.title, description: editing.description,
        price_inr: Number(editing.price_inr), price_usd: Number(editing.price_usd),
      });
      toast.success("Saved"); setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Deactivate this course?")) return;
    try { await api.delete(`/admin/courses/${id}`); toast.success("Deactivated"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-400">Set your own prices in INR + USD. Prices are dynamic — students see the amount you set here at checkout.</div>
        <Button data-testid="admin-create-course-btn" onClick={() => setCreating((c) => !c)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full"><Plus className="w-4 h-4 mr-1"/>New course</Button>
      </div>
      {creating && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title"><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="admin-new-course-title" className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Thumbnail URL"><Input value={form.thumbnail} onChange={(e)=>setForm({...form, thumbnail:e.target.value})} data-testid="admin-new-course-thumbnail" className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Description" className="md:col-span-2"><Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="admin-new-course-desc" className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Price INR"><Input type="number" value={form.price_inr} onChange={(e)=>setForm({...form, price_inr:Number(e.target.value)})} data-testid="admin-new-course-inr" className="bg-zinc-950 border-zinc-800 font-adult-mono"/></Field>
            <Field label="Price USD"><Input type="number" step="0.01" value={form.price_usd} onChange={(e)=>setForm({...form, price_usd:Number(e.target.value)})} data-testid="admin-new-course-usd" className="bg-zinc-950 border-zinc-800 font-adult-mono"/></Field>
            <Field label="Duration (weeks)"><Input type="number" value={form.duration_weeks} onChange={(e)=>setForm({...form, duration_weeks:Number(e.target.value)})} className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Level"><Input value={form.level} onChange={(e)=>setForm({...form, level:e.target.value})} className="bg-zinc-950 border-zinc-800"/></Field>
          </div>
          <div className="mt-5 flex gap-2">
            <Button onClick={create} data-testid="admin-save-new-course" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full">Save</Button>
            <Button variant="ghost" onClick={()=>setCreating(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {courses.map((c) => (
          <div key={c.course_id} className="rounded-2xl border border-zinc-800 p-5">
            <div className="flex items-start gap-4">
              <img src={c.thumbnail} alt="" className="w-20 h-20 object-cover rounded-lg border border-zinc-800"/>
              <div className="flex-1 min-w-0">
                {editing?.course_id === c.course_id ? (
                  <>
                    <Input value={editing.title} onChange={(e)=>setEditing({...editing, title:e.target.value})} className="mb-2 bg-zinc-950 border-zinc-800"/>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input type="number" value={editing.price_inr} onChange={(e)=>setEditing({...editing, price_inr:e.target.value})} placeholder="INR" className="bg-zinc-950 border-zinc-800 font-adult-mono"/>
                      <Input type="number" step="0.01" value={editing.price_usd} onChange={(e)=>setEditing({...editing, price_usd:e.target.value})} placeholder="USD" className="bg-zinc-950 border-zinc-800 font-adult-mono"/>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={save} size="sm" className="bg-amber-500 hover:bg-amber-400 text-black rounded-full">Save</Button>
                      <Button size="sm" variant="ghost" onClick={()=>setEditing(null)}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-adult-heading text-lg">{c.title}</div>
                    <div className="text-xs text-zinc-500 font-adult-mono uppercase tracking-[0.2em] mt-1">{c.level} · {c.duration_weeks} weeks</div>
                    <div className="mt-3 flex items-center gap-3 text-sm font-adult-mono">
                      <span className="text-amber-400">₹{c.price_inr?.toLocaleString?.() ?? c.price_inr}</span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-amber-400">${c.price_usd}</span>
                    </div>
                  </>
                )}
              </div>
              {editing?.course_id !== c.course_id && (
                <div className="flex flex-col gap-1">
                  <Button data-testid={`admin-edit-course-${c.course_id}`} size="icon" variant="ghost" onClick={()=>setEditing({...c})} className="h-8 w-8"><Pencil className="w-3.5 h-3.5"/></Button>
                  <Button data-testid={`admin-delete-course-${c.course_id}`} size="icon" variant="ghost" onClick={()=>remove(c.course_id)} className="h-8 w-8 text-red-400"><Trash2 className="w-3.5 h-3.5"/></Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CouponsTab({ coupons, refresh }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, currency: "usd", expires_at: "", usage_limit: "" });

  const create = async () => {
    try {
      const payload = { ...form, value: Number(form.value), usage_limit: form.usage_limit ? Number(form.usage_limit) : null };
      if (payload.type === "percent") payload.currency = null;
      if (!payload.expires_at) delete payload.expires_at;
      else payload.expires_at = new Date(payload.expires_at).toISOString();
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created"); setCreating(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (code) => {
    try { await api.post(`/admin/coupons/${code}/toggle`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try { await api.delete(`/admin/coupons/${code}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-400">Percent-off or flat-off codes. Optional expiry and usage limit.</div>
        <Button data-testid="admin-create-coupon-btn" onClick={() => setCreating((c) => !c)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full"><Plus className="w-4 h-4 mr-1"/>New coupon</Button>
      </div>
      {creating && (
        <div className="rounded-2xl border border-violet-500/40 bg-violet-500/5 p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Code"><Input data-testid="admin-new-coupon-code" value={form.code} onChange={(e)=>setForm({...form, code:e.target.value.toUpperCase()})} placeholder="LAUNCH10" className="bg-zinc-950 border-zinc-800 font-adult-mono uppercase"/></Field>
            <Field label="Type">
              <select value={form.type} onChange={(e)=>setForm({...form, type:e.target.value})} className="h-10 w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm">
                <option value="percent">Percent off</option>
                <option value="flat">Flat off</option>
              </select>
            </Field>
            <Field label={form.type === "percent" ? "Percent (1–100)" : "Amount"}><Input type="number" value={form.value} onChange={(e)=>setForm({...form, value:e.target.value})} className="bg-zinc-950 border-zinc-800 font-adult-mono"/></Field>
            {form.type === "flat" && (
              <Field label="Currency">
                <select value={form.currency} onChange={(e)=>setForm({...form, currency:e.target.value})} className="h-10 w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm">
                  <option value="usd">USD</option><option value="inr">INR</option>
                </select>
              </Field>
            )}
            <Field label="Expires (optional)"><Input type="datetime-local" value={form.expires_at} onChange={(e)=>setForm({...form, expires_at:e.target.value})} className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Usage limit (optional)"><Input type="number" value={form.usage_limit} onChange={(e)=>setForm({...form, usage_limit:e.target.value})} className="bg-zinc-950 border-zinc-800 font-adult-mono"/></Field>
          </div>
          <div className="mt-5 flex gap-2">
            <Button onClick={create} data-testid="admin-save-new-coupon" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full">Save</Button>
            <Button variant="ghost" onClick={()=>setCreating(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {coupons.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-12 text-center text-zinc-400">
          <Ticket className="w-6 h-6 mx-auto text-amber-400 mb-2"/>No coupons yet. Create one to run a launch discount.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 text-zinc-500 text-xs uppercase tracking-[0.2em] font-adult-mono">
              <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Type</th><th className="text-left p-4">Value</th><th className="text-left p-4">Used</th><th className="text-left p-4">Expires</th><th className="text-left p-4">Status</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.code} className="border-t border-zinc-900">
                  <td className="p-4 font-adult-mono text-amber-400 font-semibold">{c.code}</td>
                  <td className="p-4"><span className="text-xs font-adult-mono uppercase tracking-[0.15em] text-zinc-400">{c.type}</span></td>
                  <td className="p-4 font-adult-mono">{c.type === "percent" ? `${c.value}%` : `${c.currency === "inr" ? "₹" : "$"}${c.value}`}</td>
                  <td className="p-4 text-zinc-400">{c.times_used || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                  <td className="p-4 text-zinc-400 text-xs font-adult-mono">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4">
                    <button onClick={() => toggle(c.code)} data-testid={`admin-coupon-toggle-${c.code}`} className={`text-xs px-2 py-1 rounded-full font-adult-mono uppercase tracking-[0.15em] ${c.active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-zinc-800 text-zinc-500"}`}>
                      {c.active ? "Active" : "Off"}
                    </button>
                  </td>
                  <td className="p-4"><Button size="icon" variant="ghost" onClick={()=>remove(c.code)} className="h-8 w-8 text-red-400" data-testid={`admin-coupon-delete-${c.code}`}><Trash2 className="w-3.5 h-3.5"/></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function KidsEventsTab() {
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "Coding", title: "", start_time_iso: "", duration_min: 60, meeting_link: "", description: "", age_tier: "8-11" });

  const load = async () => {
    const { data } = await api.get("/admin/kids-sessions");
    setEvents(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.start_time_iso || !form.meeting_link) return toast.error("Title, start time, and meeting link are required");
    try {
      await api.post("/admin/kids-sessions", { ...form, duration_min: Number(form.duration_min), start_time_iso: new Date(form.start_time_iso).toISOString() });
      toast.success("Session scheduled — students with a paid pack will see it on their dashboard");
      setCreating(false); setForm({ subject: "Coding", title: "", start_time_iso: "", duration_min: 60, meeting_link: "", description: "", age_tier: "8-11" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try { await api.delete(`/admin/kids-sessions/${id}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-400">Schedule kids sessions here. They appear on the dashboard of every parent who has bought a pack in that subject.</div>
        <Button data-testid="admin-create-kids-event-btn" onClick={() => setCreating((c)=>!c)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full"><Plus className="w-4 h-4 mr-1"/>Schedule</Button>
      </div>
      {creating && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Subject">
              <select data-testid="admin-kids-event-subject" value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} className="h-10 w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm">
                <option>Coding</option><option>Photography</option><option>Aerospace</option>
              </select>
            </Field>
            <Field label="Age tier">
              <select data-testid="admin-kids-event-age" value={form.age_tier} onChange={(e)=>setForm({...form, age_tier:e.target.value})} className="h-10 w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm">
                <option value="5-8">5–8</option><option value="8-11">8–11</option><option value="11-16">11–16</option><option value="">All ages</option>
              </select>
            </Field>
            <Field label="Duration (min)"><Input type="number" value={form.duration_min} onChange={(e)=>setForm({...form, duration_min:e.target.value})} className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Title" className="md:col-span-2"><Input data-testid="admin-kids-event-title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Scratch — build your first game" className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Start time (local)"><Input data-testid="admin-kids-event-start" type="datetime-local" value={form.start_time_iso} onChange={(e)=>setForm({...form, start_time_iso:e.target.value})} className="bg-zinc-950 border-zinc-800"/></Field>
            <Field label="Meeting link" className="md:col-span-2"><Input data-testid="admin-kids-event-link" value={form.meeting_link} onChange={(e)=>setForm({...form, meeting_link:e.target.value})} placeholder="https://meet.google.com/…" className="bg-zinc-950 border-zinc-800 font-adult-mono text-xs"/></Field>
            <Field label="Description (optional)" className="md:col-span-3"><Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="bg-zinc-950 border-zinc-800"/></Field>
          </div>
          <div className="mt-5 flex gap-2">
            <Button data-testid="admin-save-kids-event" onClick={create} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full">Save</Button>
            <Button variant="ghost" onClick={()=>setCreating(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-12 text-center text-zinc-400">
          <CalendarDays className="w-6 h-6 mx-auto text-amber-400 mb-2"/>No sessions scheduled yet. Add one and parents will see it on their dashboard within seconds.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 text-zinc-500 text-xs uppercase tracking-[0.2em] font-adult-mono">
              <tr><th className="text-left p-4">Subject</th><th className="text-left p-4">Title</th><th className="text-left p-4">When</th><th className="text-left p-4">Age</th><th className="text-left p-4">Link</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.event_id} className="border-t border-zinc-900">
                  <td className="p-4 font-adult-mono text-amber-400">{e.subject}</td>
                  <td className="p-4">{e.title}</td>
                  <td className="p-4 text-zinc-400 text-xs font-adult-mono">{new Date(e.start_time_iso).toLocaleString(undefined,{dateStyle:"medium", timeStyle:"short"})} · {e.duration_min}min</td>
                  <td className="p-4 text-zinc-400">{e.age_tier || "all"}</td>
                  <td className="p-4"><a href={e.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3"/>join</a></td>
                  <td className="p-4"><Button size="icon" variant="ghost" onClick={()=>remove(e.event_id)} data-testid={`admin-delete-kids-event-${e.event_id}`} className="h-8 w-8 text-red-400"><Trash2 className="w-3.5 h-3.5"/></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FounderSettingsTab() {
  const [settings, setSettings] = useState({ youtube_channel_id: "" });
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState([]);
  const [videoStatus, setVideoStatus] = useState("idle");

  const load = async () => {
    const { data: f } = await api.get("/catalog/founder");
    setSettings({ youtube_channel_id: f.youtube_channel_id || "" });
    const { data: v } = await api.get("/catalog/founder/videos");
    setVideos(v.videos || []);
    setVideoStatus(v.channel_id ? (v.videos?.length ? "loaded" : "empty") : "unset");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/founder/settings", { youtube_channel_id: settings.youtube_channel_id.trim() || null });
      toast.success("Saved. Fetching latest videos…");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-zinc-800 p-6">
        <div className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500 mb-4 flex items-center gap-2"><Youtube className="w-3.5 h-3.5 text-red-500"/>YouTube channel</div>
        <p className="text-sm text-zinc-400 leading-relaxed">Paste your <span className="text-amber-400">YouTube channel ID</span> (starts with <span className="font-adult-mono">UC…</span>). Every new upload — including shorts — will show on the Founder page automatically.</p>
        <p className="text-xs text-zinc-500 mt-2">Find it at <a href="https://www.youtube.com/account_advanced" target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300">youtube.com/account_advanced</a></p>
        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">Channel ID</Label>
          <Input data-testid="admin-yt-channel" value={settings.youtube_channel_id} onChange={(e)=>setSettings({...settings, youtube_channel_id: e.target.value})} placeholder="UCxxxxxxxxxxxxxxxxxxxxxx" className="mt-2 bg-zinc-950 border-zinc-800 font-adult-mono"/>
        </div>
        <Button data-testid="admin-yt-save" onClick={save} disabled={busy} className="mt-4 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full">
          {busy ? "Saving…" : "Save & fetch"}
        </Button>
      </div>

      <div className="rounded-2xl border border-zinc-800 p-6">
        <div className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500 mb-4">Preview · what's live on /meet-sankalp</div>
        {videoStatus === "unset" && <div className="text-sm text-zinc-400">Set a channel ID to see videos pull in.</div>}
        {videoStatus === "empty" && <div className="text-sm text-zinc-400">Channel connected but no public videos yet.</div>}
        {videoStatus === "loaded" && (
          <div className="grid grid-cols-2 gap-3">
            {videos.slice(0, 4).map((v) => (
              <a key={v.id} href={v.url} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-zinc-800 hover:border-amber-500/60 transition-smooth">
                <img src={v.thumbnail} alt="" className="w-full aspect-video object-cover"/>
                <div className="p-2 text-xs text-zinc-300 line-clamp-2">{v.title}</div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InquiriesTab({ inquiries }) {
  const exportCsv = () => {
    if (!inquiries.length) return;
    const headers = ["created_at", "company_name", "contact_name", "designation", "email", "phone", "interest", "team_size", "message"];
    const rows = [headers.join(",")].concat(
      inquiries.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `company_inquiries_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!inquiries.length) {
    return <div data-testid="inquiries-empty" className="rounded-2xl border border-dashed border-zinc-800 p-12 text-center text-zinc-400">No company inquiries yet. Share <span className="text-amber-400 font-adult-mono">/companies</span> with prospects.</div>;
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400"><span className="text-amber-400 font-semibold">{inquiries.length}</span> total inquiries</div>
        <button data-testid="inquiries-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold px-4 py-2 transition-smooth">
          <Building2 className="w-3.5 h-3.5"/> Export CSV
        </button>
      </div>
      <div className="rounded-2xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-zinc-500 text-xs uppercase tracking-[0.2em] font-adult-mono">
            <tr>
              <th className="text-left p-4">When</th>
              <th className="text-left p-4">Company</th>
              <th className="text-left p-4">Contact</th>
              <th className="text-left p-4">Interest</th>
              <th className="text-left p-4">Team size</th>
              <th className="text-left p-4">Message</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((r) => (
              <tr key={r.inquiry_id} data-testid={`inquiry-row-${r.inquiry_id}`} className="border-t border-zinc-900 align-top">
                <td className="p-4 text-zinc-500 font-adult-mono text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-4 font-medium">{r.company_name}</td>
                <td className="p-4 text-zinc-300">
                  <div>{r.contact_name}</div>
                  {r.designation && <div className="text-xs text-zinc-500">{r.designation}</div>}
                  <a href={`mailto:${r.email}`} className="text-xs text-amber-400 hover:underline">{r.email}</a>
                  {r.phone && <div className="text-xs text-zinc-500 mt-0.5">{r.phone}</div>}
                </td>
                <td className="p-4"><span className="text-xs uppercase tracking-[0.16em] font-adult-mono rounded-full bg-violet-500/10 border border-violet-500/40 text-violet-300 px-2.5 py-1">{r.interest}</span></td>
                <td className="p-4 text-zinc-400">{r.team_size || "—"}</td>
                <td className="p-4 text-zinc-400 text-xs max-w-md whitespace-pre-wrap">{r.message || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

